import React, { useState, useEffect } from 'react';
import dbService from '../../../services/dbService';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import RuleModal from './RuleModal';
import { PencilSquare, Trash, PlusCircleFill, ArrowUpCircle, ArrowDownCircle } from 'react-bootstrap-icons';
import { Button, Tabs, Tab, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { usePermissions } from '../../../hooks/usePermissions';
import PageContainer from '../../layout/PageContainer';

const RuleList = () => {
  const [allRules, setAllRules] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [currentRule, setCurrentRule] = useState(null);
  const { permissions } = usePermissions(); // CORRECT: De-structure
  const { user } = useAuth();

  const fetchRules = async () => setAllRules(await dbService.getAll('rules'));
  useEffect(() => { fetchRules(); }, []);

const handleSave = async (ruleToSave) => {
    try {
      if (ruleToSave.id) {
        await dbService.update('rules', ruleToSave);
        toast.info('Rule updated in Sandbox.');
      } else {
        const newRule = { ...ruleToSave, environment: 'Sandbox', version: '1.0', status: 'Active' };
        await dbService.add('rules', newRule);
        toast.success('New rule created in Sandbox.');
      }
      await dbService.addLog({ user: user.name, action: 'Edited Sandbox Rule', details: `Rule: ${ruleToSave.name}` });
      setModalShow(false);
      fetchRules();
    } catch (error) { toast.error('Failed to save rule.'); }
  };
  
  const handleDeploy = async (rule) => {
      const newVersion = (parseFloat(rule.version) + 0.1).toFixed(1);
      const deployedRule = { ...rule, environment: 'Production', version: newVersion };
      await dbService.update('rules', deployedRule);
      await dbService.addLog({ user: user.name, action: 'Deployed Rule', details: `Rule: ${rule.name} to v${newVersion}` });
      toast.success(`Rule "${rule.name}" deployed to Production!`);
      fetchRules();
  };

  const handleRollback = async (rule) => {
      const rolledBackRule = { ...rule, environment: 'Sandbox' };
      await dbService.update('rules', rolledBackRule);
      await dbService.addLog({ user: user.name, action: 'Rolled Back Rule', details: `Rule: ${rule.name}` });
      toast.warn(`Rule "${rule.name}" rolled back to Sandbox.`);
      fetchRules();
  };
  
  const handleDelete = async (rule) => {
    if (window.confirm(`Are you sure you want to delete the rule: "${rule.name}"? This cannot be undone.`)) {
        await dbService.delete('rules', rule.id);
        await dbService.addLog({ user: user.name, action: 'Deleted Rule', details: `Rule: ${rule.name}` });
        toast.warn('Rule permanently deleted.');
        fetchRules();
    }
  };

  const generateLogicSummary = (rule) => {
      const { logic } = rule;
      if (logic.type === 'transaction') {
          if (logic.field === 'amount') return `(TXN) ${logic.txnType} Amount ${logic.operator} $${logic.value}`;
          if (logic.field === 'counterpartyCountry') return `(TXN) To High-Risk Country`;
      }
      if (logic.type === 'kyc') {
          return `(KYC) ${logic.field} ${logic.operator} "${logic.value}"`;
      }
      return 'N/A';
  };


  const productionRules = allRules.filter(r => r.environment === 'Production');

  const renderRuleTable = (environment) => {
      const rules = allRules.filter(r => r.environment === environment);
      return (
        <div className="table-responsive">
            <table className="table table-hover align-middle">
                <thead><tr><th>Name</th><th>Logic Summary</th><th>Version</th><th>Alert Score</th><th>Actions</th></tr></thead>
                <tbody>
                    {rules.length > 0 ? rules.map(rule => (
                        <tr key={rule.id}>
                            <td>
                                {rule.name}
                                {rule.autoCreateCase && <span className="badge bg-danger ms-2">Auto-Creates Case</span>}
                            </td>
                            <td><code>{generateLogicSummary(rule)}</code></td>
                            <td>{rule.version}</td>
                            <td><span className="badge bg-secondary">{rule.riskScore}</span></td>
                            <td>
                                {environment === 'Sandbox' && (
                                    <>
                                        <OverlayTrigger overlay={<Tooltip>Deploy to Production</Tooltip>}>
                                            <Button variant="success" size="sm" className="me-2" onClick={() => handleDeploy(rule)} disabled={!permissions.canDeployRules}><ArrowUpCircle/></Button>
                                        </OverlayTrigger>
                                        <OverlayTrigger overlay={<Tooltip>Edit Rule</Tooltip>}>
                                            <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => { setCurrentRule(rule); setModalShow(true); }} disabled={!permissions.canEditRules}><PencilSquare /></Button>
                                        </OverlayTrigger>
                                        <OverlayTrigger overlay={<Tooltip>Delete Rule</Tooltip>}>
                                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(rule)} disabled={!permissions.canDeleteRules}><Trash /></Button>
                                        </OverlayTrigger>
                                    </>
                                )}
                                {environment === 'Production' && (
                                     <OverlayTrigger overlay={<Tooltip>Rollback to Sandbox</Tooltip>}>
                                        <Button variant="warning" size="sm" onClick={() => handleRollback(rule)} disabled={!permissions.canDeployRules}><ArrowDownCircle /></Button>
                                     </OverlayTrigger>
                                )}
                            </td>
                        </tr>
                    )) : <tr><td colSpan="5" className="text-center text-muted">No rules in this environment.</td></tr>}
                </tbody>
            </table>
        </div>
      );
  };

  return (
    <PageContainer title="Rule Engine & Management">
      <div className="d-flex justify-content-between align-items-center mb-3">
        {permissions.isAdmin ? ( // Using isAdmin for simplicity, could be canCreateRules
            <Button variant="primary" onClick={() => { setCurrentRule(null); setModalShow(true); }}>
                <PlusCircleFill className="me-2" /> Create New Rule in Sandbox
            </Button>
        ) : (
            <OverlayTrigger overlay={<Tooltip>Only Admins can create new rules.</Tooltip>}>
                <span className="d-inline-block">
                    <Button variant="primary" disabled style={{ pointerEvents: 'none' }}>
                        <PlusCircleFill className="me-2" /> Create New Rule in Sandbox
                    </Button>
                </span>
            </OverlayTrigger>
        )}
      </div>
      <p>Create, test, and manage rules in a safe "Sandbox" environment before deploying them to "Production" where they will actively generate alerts.</p>
      
      <Tabs defaultActiveKey="sandbox" id="rule-environments" className="mb-3">
        <Tab eventKey="sandbox" title="🧪 Sandbox">
            <div className="p-3 border border-top-0">{renderRuleTable('Sandbox')}</div>
        </Tab>
        <Tab eventKey="production" title={`✅ Production (${productionRules.length} Active)`}>
            <div className="p-3 border border-top-0">{renderRuleTable('Production')}</div>
        </Tab>
      </Tabs>
      
       {modalShow && (
        <RuleModal
          show={modalShow}
          onHide={() => setModalShow(false)}
          onSave={handleSave}
          rule={currentRule}
        />
      )}
    </PageContainer>
  );
};

export default RuleList;